import axios from "axios";
import router from "../router";

// 统一响应包 { code, msg, data }；鉴权用 Bearer token（localStorage），不依赖 Cookie。
const TOKEN_KEY = "ceanx_admin_token";

const client = axios.create({
  baseURL: "",
  timeout: 15000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (resp) => {
    const body = resp.data || {};
    // 业务层错误：统一弹提示（登录/鉴权失败由 401 分支处理）
    if (body.code && body.code !== 200 && resp.config.method.toUpperCase() !== "GET") {
      // GET 列表类接口静默失败（页面自行展示空态）
    }
    return resp;
  },
  (error) => {
    const status = error.response && error.response.status;
    const body = (error.response && error.response.data) || {};
    // ⛔ 后端错误包是 {code,msg,data}，且 HTTP 状态码与 code 一致 → axios 直接 reject。
    //    不把 msg 抬到 error.message 的话，页面调用方只能拿到
    //    "Request failed with status code 400"，后端写的具体原因（例如
    //    「还有 2 笔待付款订单」「证件照商品由系统内置」）全部被吞掉 —— 2026-10-24 踩过。
    //    注意导出 CSV 走 blob，此时 body.msg 不存在，保持 axios 默认文案。
    if (body && typeof body.msg === "string" && body.msg) {
      error.message = body.msg;
    }
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      if (router.currentRoute.name !== "login") {
        router.replace({ name: "login" });
      }
    }
    return Promise.reject(error);
  }
);

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

// 把后端返回的 CSV（blob）落成文件下载；文件名优先取 Content-Disposition
function downloadBlob(resp, fallbackName) {
  const disposition = (resp.headers && resp.headers["content-disposition"]) || "";
  let filename = fallbackName + ".csv";
  const m = /filename="([^"]+)"/.exec(disposition);
  if (m && m[1]) filename = m[1];
  const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return filename;
}

// 统一拆包：返回 data 字段，业务错误抛 Error(msg)
async function unwrap(promise) {
  const resp = await promise;
  const body = resp.data || {};
  if (body.code && body.code !== 200) {
    throw new Error(body.msg || "请求失败");
  }
  return body.data;
}

export default {
  setToken,
  getToken,
  // ---- 鉴权
  async login(username, password) {
    const resp = await client.post("/api/auth/login", { username, password });
    const body = resp.data || {};
    if (body.code !== 200) throw new Error(body.msg || "登录失败");
    return body.data; // { userId, username, role, expiresAt, token }
  },
  async me() {
    return unwrap(client.get("/api/auth/me"));
  },
  async logout() {
    try {
      await client.post("/api/auth/logout");
    } catch (e) {
      /* 忽略 */
    }
    setToken(null);
  },
  // ---- 用户
  listUsers(params) {
    return unwrap(client.get("/api/admin/users", { params }));
  },
  setUserStatus(id, status) {
    return unwrap(client.post(`/api/admin/users/${id}/status`, { status }));
  },
  resetPassword(id, newPassword) {
    return unwrap(client.post(`/api/admin/users/${id}/password`, { newPassword }));
  },
  grant(id, payload) {
    return unwrap(client.post(`/api/admin/users/${id}/grant`, payload));
  },
  /** 用户工具币：余额 + 最近流水（对账用） */
  userCoins(id, params) {
    return unwrap(client.get(`/api/admin/users/${id}/coins`, { params }));
  },
  /** 人工加/扣工具币：payload = { delta, note }（delta 为负即扣减） */
  adjustUserCoins(id, payload) {
    return unwrap(client.post(`/api/admin/users/${id}/coins`, payload));
  },
  // ---- 系统成员管理（仅超管）
  listMembers(params) {
    return unwrap(client.get("/api/admin/members", { params }));
  },
  createMember(body) {
    return unwrap(client.post("/api/admin/members", body));
  },
  updateMember(id, body) {
    return unwrap(client.post(`/api/admin/members/${id}`, body));
  },
  resetMemberPassword(id, newPassword) {
    return unwrap(client.post(`/api/admin/members/${id}/password`, { newPassword }));
  },
  deleteMember(id) {
    return unwrap(client.delete(`/api/admin/members/${id}`));
  },
  // ---- 付费信息
  listOrders(params) {
    return unwrap(client.get("/api/admin/orders", { params }));
  },
  listEntitlements(params) {
    return unwrap(client.get("/api/admin/entitlements", { params }));
  },
  listPlans() {
    return unwrap(client.get("/api/plans"));
  },
  // ---- 日志管理
  listLogs(params) {
    return unwrap(client.get("/api/admin/logs", { params }));
  },
  logStats(params) {
    return unwrap(client.get("/api/admin/logs/stats", { params }));
  },
  // 导出 CSV（请求带 Bearer，故走 axios 而非直接 location.href）
  async exportLogs(params) {
    const resp = await client.get("/api/admin/logs/export", {
      params,
      responseType: "blob",
    });
    return downloadBlob(resp, "usage_log");
  },
  async exportAudit(params) {
    const resp = await client.get("/api/admin/audit/export", {
      params,
      responseType: "blob",
    });
    return downloadBlob(resp, "admin_audit");
  },
  // ---- 套餐管理（含上下架）
  listPlansAdmin() {
    return unwrap(client.get("/api/admin/plans"));
  },
  upsertPlan(body) {
    return unwrap(client.post("/api/admin/plans", body));
  },
  setPlanActive(code, active) {
    return unwrap(client.post(`/api/admin/plans/${code}/active`, { active }));
  },
  /** 删除前预检：返回 { deletable, blockers, usage }（点删除前先问，别让人吃 400） */
  planUsage(code) {
    return unwrap(client.get(`/api/admin/plans/${encodeURIComponent(code)}/usage`));
  },
  /** 删除套餐：后端会拦「待付款订单 / 生效中权益 / 证件照内置商品」，返回 400 带原因 */
  deletePlan(code) {
    return unwrap(client.delete(`/api/admin/plans/${encodeURIComponent(code)}`));
  },
  // ---- 客服消息 / 留言反馈（小程序「联系客服」两入口共用一条管线）
  /** 列表：params = { limit, offset, unpushed, channel, handled } */
  listSupportMessages(params) {
    return unwrap(client.get("/api/support/messages", { params }));
  },
  /** 补推未送达：body = { ids?: [...], limit?: 20 }（都不给时自动捞未推送） */
  retrySupportMessages(body) {
    return unwrap(client.post("/api/support/messages/retry", body || {}));
  },
  /** 标记已处理 / 取消：body = { id, handled }；与推送状态正交 */
  setSupportHandled(id, handled) {
    return unwrap(client.post("/api/support/messages/handled", { id, handled }));
  },
  /**
   * 拉取文件二进制（反馈截图显示用）。
   * 下载口要鉴权，而 <img src> 带不了 Authorization 头，只能先用 axios 取 blob，
   * 再转 objectURL 给 <el-image>。调用方用完必须 revokeObjectURL。
   */
  async fetchFileBlob(filePath) {
    const resp = await client.get("/api/files/download", {
      params: { filePath },
      responseType: "blob",
    });
    return resp.data;
  },
  // ---- 系统设置
  getSettings() {
    return unwrap(client.get("/api/admin/settings"));
  },
  updateSettings(payload) {
    return unwrap(client.post("/api/admin/settings", payload));
  },
  // ---- 分享卡管理（结果页「分享成果卡」的标题/封面图，保存即生效、小程序不发版）
  getShareCards() {
    return unwrap(client.get("/api/admin/share-cards"));
  },
  saveShareCards(payload) {
    return unwrap(client.post("/api/admin/share-cards", payload));
  },
  /** 上传封面图：formData 带 file 字段，返回 { url, name } */
  uploadShareCover(file) {
    const fd = new FormData();
    fd.append("file", file);
    return unwrap(client.post("/api/admin/share-cards/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }));
  },
  /**
   * 小程序当前实际生效的支付通道（只读探测，用于后台核对配置结果）。
   * 返回 { preferred, available, mode } —— 与小程序端读的是同一个接口，
   * 所以后台看到什么，用户端就是什么。
   */
  payChannels() {
    return unwrap(client.get("/api/mini/pay/channels"));
  },
  // ---- 增长副驾（只读：内核状态 / 周报门槛指标 / 选题池 / 内容初稿）
  // 计算发生在服务器上的 CLI，这里只读结果；点刷新不会触发任何计算。
  getGrowthHealth() {
    return unwrap(client.get("/api/admin/growth/health"));
  },
  getGrowthReport() {
    return unwrap(client.get("/api/admin/growth/report"));
  },
  getGrowthTopics(limit) {
    return unwrap(client.get("/api/admin/growth/topics", { params: { limit: limit || 30 } }));
  },
  getGrowthDrafts(params) {
    return unwrap(client.get("/api/admin/growth/drafts", { params: params || {} }));
  },
  /** 采纳 / 毙掉 / 重置为待审。只标记状态，不发布内容。 */
  setGrowthDraftStatus(id, status, note) {
    return unwrap(client.post(`/api/admin/growth/drafts/${id}/status`, { status, note }));
  },
  // ---- 概览 / 审计
  stats() {
    return unwrap(client.get("/api/admin/stats"));
  },
  /**
   * 上游通道健康快照（2026-10-28）。
   * 返回 { channels: { matting: [...], ocr: [...] }, alerts: {...} }，
   * 数据来自后端常驻探活的内存状态，读它不会额外请求 GPU 机。
   */
  channels() {
    return unwrap(client.get("/api/admin/channels"));
  },
  listAudit(params) {
    return unwrap(client.get("/api/admin/audit", { params }));
  },
};

export { TOKEN_KEY };
